import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SectionManifest, ViewDSL } from '../types';
import { useAgentStore } from '../state/agent-store';

export interface ProjectData {
  sections: SectionManifest[];
  views: ViewDSL[];
  tokens: Record<string, any>;
  projectPath: string;
}

export interface UseProjectDataOptions {
  projectPath?: string;
  enabled?: boolean;
}

// Mock data loader - replace with actual file system operations
async function loadProjectData(projectPath: string): Promise<ProjectData> {
  // TODO: Implement actual file system loading
  // This would read from:
  // - src/sections/*.json for section manifests
  // - src/views/*.json for view DSLs  
  // - src/tokens/index.json for design tokens
  
  // For now, return empty project data
  return {
    sections: [],
    views: [],
    tokens: {},
    projectPath
  };
}

async function saveProjectData(data: ProjectData): Promise<void> {
  // TODO: Implement actual file system saving
  console.log('Saving project data:', data);
}

export function useProjectData(options: UseProjectDataOptions = {}) {
  const queryClient = useQueryClient();
  const { currentProject, updateProject } = useAgentStore();
  
  const projectPath = options.projectPath || currentProject.projectPath;

  // Load project data query
  const projectQuery = useQuery({
    queryKey: ['project-data', projectPath],
    queryFn: () => loadProjectData(projectPath),
    enabled: options.enabled !== false && Boolean(projectPath),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    onSuccess: (data) => {
      // Sync with Zustand store
      updateProject(data);
    }
  });

  // Save project data mutation
  const saveProjectMutation = useMutation({
    mutationFn: saveProjectData,
    onSuccess: () => {
      // Invalidate and refetch project data
      queryClient.invalidateQueries({ queryKey: ['project-data', projectPath] });
    },
    onError: (error) => {
      console.error('Failed to save project data:', error);
    }
  });

  // Individual queries for performance
  const sectionsQuery = useQuery({
    queryKey: ['project-sections', projectPath],
    queryFn: async () => {
      const data = await loadProjectData(projectPath);
      return data.sections;
    },
    enabled: options.enabled !== false && Boolean(projectPath),
    staleTime: 5 * 60 * 1000
  });

  const viewsQuery = useQuery({
    queryKey: ['project-views', projectPath],
    queryFn: async () => {
      const data = await loadProjectData(projectPath);
      return data.views;
    },
    enabled: options.enabled !== false && Boolean(projectPath),
    staleTime: 5 * 60 * 1000
  });

  const tokensQuery = useQuery({
    queryKey: ['project-tokens', projectPath],
    queryFn: async () => {
      const data = await loadProjectData(projectPath);
      return data.tokens;
    },
    enabled: options.enabled !== false && Boolean(projectPath),
    staleTime: 5 * 60 * 1000
  });

  // Helper functions
  const saveProject = (data?: Partial<ProjectData>) => {
    const projectData: ProjectData = {
      ...currentProject,
      ...data
    };
    return saveProjectMutation.mutateAsync(projectData);
  };

  const refetchProject = () => {
    return queryClient.invalidateQueries({ queryKey: ['project-data', projectPath] });
  };

  // Find section by name or id
  const findSection = (identifier: string): SectionManifest | undefined => {
    return currentProject.sections.find(
      section => section.name === identifier || section.id === identifier
    );
  };

  // Get sections by category
  const getSectionsByCategory = (category: 'element' | 'component' | 'section') => {
    return currentProject.sections.filter(section => section.category === category);
  };

  // Get section variants
  const getSectionVariants = (sectionName: string) => {
    const section = findSection(sectionName);
    return section?.variants || [];
  };

  // Check if section exists
  const sectionExists = (name: string): boolean => {
    return Boolean(findSection(name));
  };

  // Get available tokens
  const getAvailableTokens = () => {
    return Object.keys(currentProject.tokens);
  };

  // Find similar section names (for reuse-first suggestions)
  const findSimilarSections = (name: string, threshold: number = 0.7) => {
    return currentProject.sections.filter(section => {
      const similarity = calculateSimilarity(name.toLowerCase(), section.name.toLowerCase());
      return similarity > threshold;
    });
  };

  // Simple string similarity calculation
  const calculateSimilarity = (str1: string, str2: string): number => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    return (longer.length - levenshteinDistance(longer, shorter)) / longer.length;
  };

  const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  };

  return {
    // Query states
    data: currentProject,
    isLoading: projectQuery.isLoading,
    isError: projectQuery.isError,
    error: projectQuery.error,
    
    // Individual data
    sections: currentProject.sections,
    views: currentProject.views,
    tokens: currentProject.tokens,
    
    // Loading states for individual data
    sectionsLoading: sectionsQuery.isLoading,
    viewsLoading: viewsQuery.isLoading,
    tokensLoading: tokensQuery.isLoading,
    
    // Mutations
    saveProject,
    isSaving: saveProjectMutation.isPending,
    saveError: saveProjectMutation.error,
    
    // Utilities
    refetchProject,
    findSection,
    getSectionsByCategory,
    getSectionVariants,
    sectionExists,
    getAvailableTokens,
    findSimilarSections,
    
    // Project path
    projectPath
  };
}